import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type {
  CompactHubSchedule,
  DateRange,
  HubEvent,
  PendingSchedule,
  PendingScheduleCycle,
  ResolvedSchedule,
  SchedulePeriod,
} from './types';

dayjs.extend(utc);

/**
 * 시간 설정 — 항상 KST 기준으로 해석.
 * 배포 컨테이너는 UTC로 도는데 실행 환경 타임존에 맡기면 서버·브라우저 결과가 9시간 갈리므로
 * +09:00을 문자열에 박아 고정한다.
 */
export function parseDateTime(value: string, endOfDay = false): Date {
  const [datePart, timePart] = value.trim().split(/[T\s]+/);
  const time = timePart ?? (endOfDay ? '23:59:59.999' : '00:00:00');
  const date = new Date(`${datePart}T${time}+09:00`);

  if (Number.isNaN(date.getTime())) {
    throw new Error(
      `[HUB_EVENTS] 날짜 형식 오류: '${value}' — 'YYYY-MM-DD' 또는 'YYYY-MM-DD HH:mm:ss'로 작성`,
    );
  }

  return date;
}

function toDateOnlyKst(value: string | Date): string {
  if (typeof value === 'string') {
    return value.trim().split(/[T\s]+/)[0];
  }

  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(value);
}

function formatUtcDate(date: dayjs.Dayjs): string {
  return date.format('YYYY-MM-DD');
}

/** pending / auto 윈도우 안인지 (start 필수, end 없으면 start 이후 상시) */
export function isWithinScheduleWindow(period: SchedulePeriod, now: Date): boolean {
  const start = parseDateTime(period.start);
  if (now < start) return false;
  if (!period.end) return true;

  const end = parseDateTime(period.end, true);
  return now <= end;
}

function clampPeriodToWindow(period: DateRange, window: SchedulePeriod): DateRange {
  const windowStart = parseDateTime(window.start);
  const periodStart = parseDateTime(period.start);
  const clamped: DateRange = { ...period };

  if (periodStart < windowStart) {
    clamped.start = window.start.trim().split(/[T\s]+/)[0];
  }

  if (window.end) {
    const windowEnd = parseDateTime(window.end, true);
    const periodEnd = period.end ? parseDateTime(period.end, true) : windowEnd;
    if (periodEnd > windowEnd) {
      clamped.end = window.end.trim().split(/[T\s]+/)[0];
    }
  }

  return clamped;
}

/** KST 달력 월 1일~말일 */
function computeCalendarMonthPeriod(now: Date): DateRange {
  const nowDate = toDateOnlyKst(now);
  const start = dayjs.utc(nowDate).startOf('month');
  const end = start.endOf('month');

  return {
    start: formatUtcDate(start),
    end: formatUtcDate(end),
  };
}

/** window.start부터 now까지 cycle(n일 | 'month') 단위로 현재 주기 period 계산 */
export function computeAutoPeriod(
  window: SchedulePeriod,
  cycle: PendingScheduleCycle,
  now: Date,
): DateRange {
  if (cycle === 'month') {
    return clampPeriodToWindow(computeCalendarMonthPeriod(now), window);
  }

  if (cycle < 1) {
    throw new Error(`[HUB_EVENTS] schedule.cycle은 1 이상이어야 합니다: ${cycle}`);
  }

  const anchorDate = toDateOnlyKst(window.start);
  const nowDate = toDateOnlyKst(now);
  const elapsedDays = dayjs.utc(nowDate).diff(dayjs.utc(anchorDate), 'day');
  const cycleIndex = Math.floor(elapsedDays / cycle);
  const start = dayjs.utc(anchorDate).add(cycleIndex * cycle, 'day');
  const end = start.add(cycle - 1, 'day');

  return clampPeriodToWindow(
    {
      start: formatUtcDate(start),
      end: formatUtcDate(end),
    },
    window,
  );
}

function isCompactSchedule(schedule: HubEvent['schedule']): schedule is CompactHubSchedule {
  return 'start' in schedule;
}

function toDateRange(period: SchedulePeriod): DateRange {
  return period.end ? { start: period.start, end: period.end } : { start: period.start };
}

function getBaseScheduleParts(event: HubEvent): Pick<ResolvedSchedule, 'period' | 'recruitment'> {
  const { schedule } = event;

  if (isCompactSchedule(schedule)) {
    return {
      period: toDateRange({ start: schedule.start, end: schedule.end }),
      recruitment: schedule.recruitment,
    };
  }

  return {
    period: schedule.period,
    recruitment: schedule.recruitment,
  };
}

function getPendingSchedule(event: HubEvent): PendingSchedule | undefined {
  if (event.pendingSchedule) return event.pendingSchedule;

  const { schedule } = event;
  if (!isCompactSchedule(schedule) || !schedule.pending) return undefined;

  const { pending } = schedule;
  return {
    period: { start: pending.start, end: pending.end },
    recruitment: pending.recruitment,
  };
}

function resolveCompactSchedule(
  schedule: CompactHubSchedule,
  base: Pick<ResolvedSchedule, 'period' | 'recruitment'>,
  now: Date,
): ResolvedSchedule {
  const pending = schedule.pending;
  const pendingWindow: SchedulePeriod | undefined = pending
    ? { start: pending.start, end: pending.end }
    : undefined;

  if (pendingWindow && pending && isWithinScheduleWindow(pendingWindow, now)) {
    return {
      period: toDateRange(pendingWindow),
      recruitment: pending.recruitment ?? base.recruitment,
      source: 'pending',
    };
  }

  if (schedule.cycle) {
    const cycleWindow: SchedulePeriod = { start: schedule.start, end: schedule.end };
    if (isWithinScheduleWindow(cycleWindow, now)) {
      return {
        period: computeAutoPeriod(cycleWindow, schedule.cycle, now),
        recruitment: base.recruitment,
        source: 'auto',
      };
    }
  }

  return {
    period: base.period,
    recruitment: base.recruitment,
    source: 'base',
  };
}

export function resolveSchedule(event: HubEvent, now = new Date()): ResolvedSchedule {
  const base = getBaseScheduleParts(event);

  if (isCompactSchedule(event.schedule)) {
    return resolveCompactSchedule(event.schedule, base, now);
  }

  const pendingSchedule = getPendingSchedule(event);

  if (pendingSchedule && isWithinScheduleWindow(pendingSchedule.period, now)) {
    const { period, cycle, recruitment: pendingRecruitment } = pendingSchedule;

    return {
      period: cycle ? computeAutoPeriod(period, cycle, now) : period,
      recruitment: pendingRecruitment ?? base.recruitment,
      source: cycle ? 'auto' : 'pending',
    };
  }

  return {
    period: base.period,
    recruitment: base.recruitment,
    source: 'base',
  };
}

/** 정렬·키·이탈팝업용 primary range — period 우선, 없으면 recruitment */
export function getSchedulePrimaryRange(resolved: Pick<ResolvedSchedule, 'period' | 'recruitment'>): DateRange {
  if (resolved.period?.start) return resolved.period;
  if (resolved.recruitment) return resolved.recruitment;
  return { start: '' };
}

export function getScheduleSortStart(resolved: Pick<ResolvedSchedule, 'period' | 'recruitment'>): string {
  return resolved.period?.start ?? resolved.recruitment?.start ?? '';
}

export function getScheduleSortEnd(resolved: Pick<ResolvedSchedule, 'period' | 'recruitment'>): string | undefined {
  return resolved.period?.end ?? resolved.recruitment?.end;
}

/** 이탈팝업 등 — period 우선, 없으면 recruitment */
export function getScheduleForHubPopup(
  resolved: Pick<ResolvedSchedule, 'period' | 'recruitment'>,
): DateRange | undefined {
  const range = resolved.period?.start ? resolved.period : resolved.recruitment;
  if (!range?.start) return undefined;
  return range;
}

export function hasSchedulePeriod(resolved: Pick<ResolvedSchedule, 'period'>): boolean {
  return resolved.period != null && resolved.period.start !== '';
}
